import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import storePricesService from "../../api/services/storePricesService";

const THEME = { primary: "#FB565F" };
const inputCls =
  "border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-300 focus:border-red-300 outline-none w-full";

const HIGHLIGHT_CODES = new Set(["US", "USA", "PK", "PAK", "AE", "ARE", "GB", "GBR", "SA", "SAU", "IN", "IND"]);

function getAdminRole() {
  return localStorage.getItem("adminUserRole") || "";
}

function canSeeProductId() {
  const role = getAdminRole();
  return role === "Admin" || role === "Developer";
}

function friendlyProductName(productId) {
  const id = String(productId || "").toLowerCase();
  if (id.includes("week")) return "Weekly Premium";
  if (id.includes("quarter") || id.includes("three") || id.includes("3")) {
    return "Quarterly Premium";
  }
  if (id.includes("month")) return "Monthly Premium";
  return productId || "Product";
}

function formatProductOptionLabel(p, showProductId) {
  const name = p.name || friendlyProductName(p.productId);
  if (!showProductId) return name;
  const plan = p.basePlanId ? ` / ${p.basePlanId}` : "";
  return `${name} (${p.productId}${plan})`;
}

function formatPrice(amount, currency) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return `${amount}${currency ? ` ${currency}` : ""}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: currency ? "currency" : "decimal",
      currency: currency || undefined,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}${
      currency ? ` ${currency}` : ""
    }`;
  }
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
        active ? "text-white shadow-sm" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
      }`}
      style={active ? { backgroundColor: THEME.primary } : {}}
    >
      {children}
    </button>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-gray-100 px-4 py-3 min-w-[120px] shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="text-lg font-bold text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}

function FilterBar({
  products,
  productId,
  setProductId,
  loadingProducts,
  showProductId,
  search,
  setSearch,
  currency,
  setCurrency,
  currencies,
  sortBy,
  setSortBy,
  onRefresh,
  refreshing,
}) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
        <div className="lg:col-span-3">
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Plan</label>
          <select
            className={inputCls}
            value={productId}
            disabled={loadingProducts}
            onChange={(e) => setProductId(e.target.value)}
          >
            {!products.length && <option value="">No products</option>}
            {products.map((p) => (
              <option key={p.subscriptionId || p.productId} value={p.productId}>
                {formatProductOptionLabel(p, showProductId)}
              </option>
            ))}
          </select>
        </div>
        <div className="lg:col-span-4">
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Search country</label>
          <input
            className={inputCls}
            placeholder="e.g. Pakistan, US, AED…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="lg:col-span-2">
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Currency</label>
          <select className={inputCls} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="all">All</option>
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="lg:col-span-2">
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Sort</label>
          <select className={inputCls} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="country">Country A–Z</option>
            <option value="price">Price low → high</option>
            <option value="currency">Currency</option>
          </select>
        </div>
        <div className="lg:col-span-1">
          <button
            type="button"
            className="w-full px-3 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: THEME.primary }}
            onClick={onRefresh}
            disabled={refreshing || !productId}
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

function PricesTable({
  storeLabel,
  meta,
  showProductId,
  filtered,
  prices,
  loading,
  search,
  setSearch,
  currency,
  setCurrency,
}) {
  const stats = useMemo(() => {
    const currSet = new Set(filtered.map((r) => r.currency).filter(Boolean));
    const singleCurrency = currSet.size === 1 ? Array.from(currSet)[0] : null;
    const amounts = singleCurrency
      ? filtered
          .filter((r) => r.currency === singleCurrency)
          .map((r) => Number(r.customerPrice))
          .filter((n) => !Number.isNaN(n))
      : [];
    return {
      shown: filtered.length,
      total: prices.length,
      currencies: currSet.size,
      min: amounts.length ? Math.min(...amounts) : null,
      max: amounts.length ? Math.max(...amounts) : null,
      sampleCurrency: singleCurrency,
    };
  }, [filtered, prices]);

  const title = meta
    ? meta.subscriptionName || friendlyProductName(meta.productId)
    : storeLabel;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <StatPill label="Showing" value={`${stats.shown}${stats.shown !== stats.total ? ` / ${stats.total}` : ""}`} />
        <StatPill label="Currencies" value={String(stats.currencies || "—")} />
        <StatPill
          label="Lowest"
          value={
            stats.min != null
              ? formatPrice(stats.min, stats.sampleCurrency)
              : stats.currencies > 1
                ? "Filter currency"
                : "—"
          }
        />
        <StatPill
          label="Highest"
          value={
            stats.max != null
              ? formatPrice(stats.max, stats.sampleCurrency)
              : stats.currencies > 1
                ? "Filter currency"
                : "—"
          }
        />
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2 bg-white">
          <div>
            <p className="text-base font-bold text-gray-900">{title}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {storeLabel} list price by country
              {showProductId && meta?.productId ? ` · ${meta.productId}` : ""}
              {showProductId && meta?.basePlanId ? ` / ${meta.basePlanId}` : ""}
            </p>
          </div>
          {(search || currency !== "all") && (
            <button
              type="button"
              className="text-xs font-semibold text-gray-500 hover:text-gray-800 underline"
              onClick={() => {
                setSearch("");
                setCurrency("all");
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="overflow-x-auto max-h-[65vh]">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur border-b border-gray-100">
              <tr>
                <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  #
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  Country
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  Currency
                </th>
                <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  Customer pays
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-5 py-16 text-center text-gray-400">
                    <div className="inline-flex flex-col items-center gap-2">
                      <div
                        className="h-8 w-8 rounded-full border-2 border-t-transparent animate-spin"
                        style={{ borderColor: `${THEME.primary}33`, borderTopColor: THEME.primary }}
                      />
                      <span>Loading {storeLabel.toLowerCase()} prices…</span>
                    </div>
                  </td>
                </tr>
              ) : !filtered.length ? (
                <tr>
                  <td colSpan={4} className="px-5 py-16 text-center text-gray-400">
                    No countries match your search or currency filter.
                  </td>
                </tr>
              ) : (
                filtered.map((row, index) => {
                  const highlight = HIGHLIGHT_CODES.has(String(row.countryCode || "").toUpperCase());
                  return (
                    <tr
                      key={row.countryCode}
                      className={`border-t border-gray-50 transition-colors ${
                        highlight ? "bg-rose-50/40 hover:bg-rose-50/70" : "hover:bg-gray-50/80"
                      }`}
                    >
                      <td className="px-5 py-3.5 text-xs text-gray-300 font-medium tabular-nums">
                        {index + 1}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900">
                            {row.countryName || "Unknown"}
                          </span>
                          <span className="text-[11px] text-gray-400 font-mono mt-0.5">
                            {row.countryCode || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                          {row.currency || "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span
                          className="inline-block text-base font-bold tabular-nums"
                          style={{ color: THEME.primary }}
                        >
                          {formatPrice(row.customerPrice, row.currency)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function usePriceFilters(prices) {
  const [search, setSearch] = useState("");
  const [currency, setCurrency] = useState("all");
  const [sortBy, setSortBy] = useState("country");

  const currencies = useMemo(() => {
    const set = new Set(prices.map((p) => p.currency).filter(Boolean));
    return Array.from(set).sort();
  }, [prices]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = prices.filter((p) => {
      if (currency !== "all" && p.currency !== currency) return false;
      if (!q) return true;
      return (
        String(p.countryName || "").toLowerCase().includes(q) ||
        String(p.countryCode || "").toLowerCase().includes(q) ||
        String(p.currency || "").toLowerCase().includes(q) ||
        String(p.customerPrice || "").includes(q)
      );
    });

    rows = [...rows].sort((a, b) => {
      if (sortBy === "price") {
        return Number(a.customerPrice || 0) - Number(b.customerPrice || 0);
      }
      if (sortBy === "currency") {
        return String(a.currency || "").localeCompare(String(b.currency || ""));
      }
      return String(a.countryName || a.countryCode || "").localeCompare(
        String(b.countryName || b.countryCode || "")
      );
    });
    return rows;
  }, [prices, search, currency, sortBy]);

  return {
    search,
    setSearch,
    currency,
    setCurrency,
    sortBy,
    setSortBy,
    currencies,
    filtered,
  };
}

function SetupBanner({ title, message, missing = [] }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-950">
      <p className="font-semibold">{title}</p>
      <p className="mt-1 text-amber-900">{message}</p>
      {missing.length > 0 && (
        <p className="mt-2 text-xs font-mono text-amber-800">
          Missing in backend `.env`: {missing.join(", ")}
        </p>
      )}
      <p className="mt-2 text-xs text-amber-800">
        Add the values from App Store Connect → Users and Access → Integrations → App Store Connect API, then restart
        the backend server.
      </p>
    </div>
  );
}

function AppStoreTab() {
  const showProductId = canSeeProductId();
  const [appStoreStatus, setAppStoreStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [meta, setMeta] = useState(null);
  const [prices, setPrices] = useState([]);
  const filters = usePriceFilters(prices);

  const isConfigured = appStoreStatus?.configured !== false;

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const status = await storePricesService.getAppStoreStatus();
      setAppStoreStatus(status);
      if (!status?.configured) {
        setProducts([]);
        setProductId("");
        return;
      }
      const data = await storePricesService.listAppStoreProducts();
      const list = data.products || [];
      setProducts(list);
      setProductId((prev) => {
        if (prev) return prev;
        const preferred =
          list.find((p) => p.productId === "com.nuae.naseebi.Monthly") ||
          list.find((p) => String(p.productId || "").toLowerCase().includes("month")) ||
          list[0];
        return preferred?.productId || "";
      });
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || "Failed to load App Store products");
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const loadPrices = useCallback(async (id, { refresh = false } = {}) => {
    if (!id) {
      setPrices([]);
      setMeta(null);
      return;
    }
    setLoadingPrices(true);
    try {
      const data = await storePricesService.getAppStorePrices(
        id,
        refresh ? { refresh: true } : {}
      );
      setMeta(data);
      setPrices(data.prices || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || "Failed to load prices");
      setPrices([]);
      setMeta(null);
    } finally {
      setLoadingPrices(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (productId && isConfigured) loadPrices(productId);
  }, [productId, loadPrices, isConfigured]);

  return (
    <div className="space-y-4">
      {!loadingProducts && !isConfigured && (
        <SetupBanner
          title="App Store Connect is not configured"
          message={appStoreStatus?.message || "Add App Store Connect API credentials to load prices."}
          missing={appStoreStatus?.missing || ["ASC_ISSUER_ID", "ASC_KEY_ID", "ASC_PRIVATE_KEY"]}
        />
      )}
      <FilterBar
        products={products}
        productId={productId}
        setProductId={setProductId}
        loadingProducts={loadingProducts}
        showProductId={showProductId}
        search={filters.search}
        setSearch={filters.setSearch}
        currency={filters.currency}
        setCurrency={filters.setCurrency}
        currencies={filters.currencies}
        sortBy={filters.sortBy}
        setSortBy={filters.setSortBy}
        onRefresh={() => loadPrices(productId, { refresh: true })}
        refreshing={loadingPrices}
      />
      <PricesTable
        storeLabel="App Store"
        meta={meta}
        showProductId={showProductId}
        filtered={filters.filtered}
        prices={prices}
        loading={isConfigured && (loadingPrices || loadingProducts)}
        search={filters.search}
        setSearch={filters.setSearch}
        currency={filters.currency}
        setCurrency={filters.setCurrency}
      />
    </div>
  );
}

function PlayStoreTab() {
  const showProductId = canSeeProductId();
  const PREFERRED = ["monthly_premium", "weekly_premium", "quarter_premium"];
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [meta, setMeta] = useState(null);
  const [prices, setPrices] = useState([]);
  const filters = usePriceFilters(prices);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const data = await storePricesService.listPlayStoreProducts();
      const list = data.products || [];
      setProducts(list);
      setProductId((prev) => {
        if (prev) return prev;
        const preferred = list.find((p) => PREFERRED.includes(p.productId)) || list[0];
        return preferred?.productId || "";
      });
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || "Failed to load Play Store products");
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const loadPrices = useCallback(async (id, { refresh = false } = {}) => {
    if (!id) {
      setPrices([]);
      setMeta(null);
      return;
    }
    setLoadingPrices(true);
    try {
      const data = await storePricesService.getPlayStorePrices(
        id,
        refresh ? { refresh: true } : {}
      );
      setMeta(data);
      setPrices(data.prices || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || "Failed to load Play prices");
      setPrices([]);
      setMeta(null);
    } finally {
      setLoadingPrices(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (productId) loadPrices(productId);
  }, [productId, loadPrices]);

  return (
    <div className="space-y-4">
      <FilterBar
        products={products}
        productId={productId}
        setProductId={setProductId}
        loadingProducts={loadingProducts}
        showProductId={showProductId}
        search={filters.search}
        setSearch={filters.setSearch}
        currency={filters.currency}
        setCurrency={filters.setCurrency}
        currencies={filters.currencies}
        sortBy={filters.sortBy}
        setSortBy={filters.setSortBy}
        onRefresh={() => loadPrices(productId, { refresh: true })}
        refreshing={loadingPrices}
      />
      <PricesTable
        storeLabel="Play Store"
        meta={meta}
        showProductId={showProductId}
        filtered={filters.filtered}
        prices={prices}
        loading={loadingPrices || loadingProducts}
        search={filters.search}
        setSearch={filters.setSearch}
        currency={filters.currency}
        setCurrency={filters.setCurrency}
      />
    </div>
  );
}

export default function NasePricesPage() {
  const [tab, setTab] = useState("appstore");

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nase Prices</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            See what customers pay for Weekly, Monthly, and Quarterly plans in each country.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <TabBtn active={tab === "appstore"} onClick={() => setTab("appstore")}>
            App Store
          </TabBtn>
          <TabBtn active={tab === "playstore"} onClick={() => setTab("playstore")}>
            Play Store
          </TabBtn>
        </div>
      </div>

      {tab === "appstore" ? <AppStoreTab /> : <PlayStoreTab />}
    </div>
  );
}
